import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [mustChangePassword, setMustChangePassword] = useState(
    localStorage.getItem('mustChangePassword') === 'true'
  );

  useEffect(() => {
    if (token) {
      checkAuth();
    } else {
      setLoading(false);
    }
  }, [token]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      
      // Check if user must change password from stored flag
      const storedMustChange = localStorage.getItem('mustChangePassword') === 'true';
      setMustChangePassword(storedMustChange);
    } catch (error) {
      console.error('Auth check failed:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const formData = new FormData();
    formData.append('username', email);
    formData.append('password', password);

    const response = await axios.post(`${API}/auth/login`, formData);
    const { access_token, must_change_password } = response.data;
    
    localStorage.setItem('token', access_token);
    setToken(access_token);
    
    // Store must_change_password flag
    if (must_change_password) {
      localStorage.setItem('mustChangePassword', 'true');
      setMustChangePassword(true);
    } else {
      localStorage.removeItem('mustChangePassword');
      setMustChangePassword(false);
    }
    
    const userResponse = await axios.get(`${API}/auth/me`, {
      headers: { Authorization: `Bearer ${access_token}` }
    });
    setUser(userResponse.data);
    
    return { user: userResponse.data, mustChangePassword: must_change_password };
  };

  const register = async (userData) => {
    await axios.post(`${API}/auth/register`, userData);
    return await login(userData.email, userData.password);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('mustChangePassword');
    setToken(null);
    setUser(null);
    setMustChangePassword(false);
  };

  const resetPassword = async (email) => {
    await axios.post(`${API}/auth/reset-password`, { email });
  };

  const forceChangePassword = async (newPassword) => {
    await axios.post(`${API}/auth/force-change-password`, 
      { new_password: newPassword },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    localStorage.removeItem('mustChangePassword');
    setMustChangePassword(false);
  };

  const clearMustChangePassword = () => {
    localStorage.removeItem('mustChangePassword');
    setMustChangePassword(false);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      login, 
      register, 
      logout, 
      resetPassword, 
      forceChangePassword,
      clearMustChangePassword,
      loading, 
      token,
      mustChangePassword 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
